import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "email-items-header-container" }
const _hoisted_2 = { class: "button-container" }

import { computed, onMounted, PropType, ref, watch } from "vue";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import { SetupFeeGridItem } from "@/types/tenancies/SetupFeeGridItem";
import { formatNumberIntoCurrency } from "@/utils/CurrencyFormatter";
import { CurrentUser } from "@/types/user/CurrentUser";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
import SetupFeeModal from "@/components/tenancies/pre-tenancies/setup-fee-modal.vue";
import DxButton from "devextreme-vue/button";
import GridMenu from "@/components/ui/grid/grid-menu.vue";
import { SetupFee } from "@/types/tenancies/PreTenancyItem";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import moment from "moment";


export default /*#__PURE__*/_defineComponent({
  __name: 'setup-fee-grid',
  props: {
  /**
   * A numeric value used to force updates or re-renders in the component.
   * @type {Number}
   * @required
   */
  forceUpdateValue: {
    type: Number,
    required: true,
  },
  /**
   * The array of setup fee grid items passed from the parent component.
   * @type {SetupFeeGridItem[]}
   * @required
   */
  passedSetupFeeGridItems: {
    type: Object as PropType<SetupFeeGridItem[]>,
    required: true,
  },
  /**
   * The height of grid.
   * @type {String}
   * @default 80vh
   */
  gridHeight: {
    type: String,
    default: "80vh",
  },
},
  emits: [
  /**
   * Emitted for v-model binding updates to synchronize the total amount with the parent.
   * @param {number} value - The new total gross amount.
   */
  "update:totalAmount",

  /**
   * Emitted for v-model binding updates to synchronize the setup fees with the parent.
   * @param {number} value - The new setup fees.
   */
  "update:setupFees",
],
  setup(__props, { emit }) {

const props = __props;





const currentUserStore = useCurrentUserStore();
const currentUserDetails = ref<CurrentUser>();
const selectedRecord = ref<SetupFeeGridItem | undefined>(undefined);
const setupFeeGridItems = ref<SetupFeeGridItem[]>([]);
const loading = ref(false);
const displaySetupFeeModal = ref(false);
const contextMenuId = ref("setup-fee-grid-items");
const keySetupFeeModal = ref(0);

onMounted(async () => {
  currentUserDetails.value = await currentUserStore.getCurrentUserDetails();
});

watch(
  () => props.forceUpdateValue,
  () => {
    loading.value = true;
    selectedRecord.value = undefined;
    contextMenuId.value = `${contextMenuId.value}-${moment().unix()}`;
    setTimeout(() => {
      loading.value = false;
      setupFeeGridItems.value = [...props.passedSetupFeeGridItems];
    }, 1000);
  },
  { immediate: true },
);

const totalGross = computed<number>(() => {
  return setupFeeGridItems.value.reduce((sum, item) => sum + item.gross, 0);
});

const setupFees = computed<SetupFee[]>(() => {
  return setupFeeGridItems.value
    .filter((item) => item.gross > 0)
    .map((item) => ({
      amount: item.gross,
      productId: item.productId,
      taxRateId: item.taxRateId,
      taxRate: item.taxRate,
    }));
});

watch(totalGross, (newTotal) => {
  emit("update:totalAmount", newTotal);
});

watch(setupFees, (newFees) => {
  emit("update:setupFees", newFees);
});

const handleFeeModalChange = (newItem: SetupFeeGridItem) => {
  const indexGrid = setupFeeGridItems.value.findIndex(
    (item) => item.productId === newItem.productId,
  );

  if (indexGrid !== -1) {
    // Update existing item reactively
    setupFeeGridItems.value.splice(indexGrid, 1, newItem);
  } else {
    // Add new item reactively
    setupFeeGridItems.value.push(newItem);
  }

  setupFeeGridItems.value = [...setupFeeGridItems.value];
};

const onRowClick = async (e: SetupFeeGridItem | undefined) => {
  if (!e) {
    selectedRecord.value = undefined;
    return;
  }
  selectedRecord.value = e;
  displaySetupFeeModal.value = true;
};

const onRowSelected = (e: SetupFeeGridItem | undefined) => {
  if (!e) {
    selectedRecord.value = undefined;
    return;
  }

  selectedRecord.value = e;
};

const handleAddClick = () => {
  keySetupFeeModal.value++;
  displaySetupFeeModal.value = true;
};

const handleEditClick = () => {
  if (!selectedRecord.value) return;

  displaySetupFeeModal.value = true;
};

const handleDeleteClick = () => {
  if (!selectedRecord.value) return;

  setupFeeGridItems.value = setupFeeGridItems.value.filter(
    (item) => item.productId !== selectedRecord.value?.productId,
  );
};

const formatVATValue = (data: SetupFeeGridItem) => {
  // Amount / Vat
  if (!currentUserDetails.value) return "";
  return formatNumberIntoCurrency(
    data.vat,
    currentUserDetails.value.countryCodeID,
  );
};
const formatNetValue = (data: SetupFeeGridItem) => {
  // Amount / Net
  if (!currentUserDetails.value) return "";

  return formatNumberIntoCurrency(
    data.net,
    currentUserDetails.value.countryCodeID,
  );
};

const formatGrossValue = (data: SetupFeeGridItem) => {
  // Amount / Net
  if (!currentUserDetails.value) return "";

  return formatNumberIntoCurrency(
    data.gross,
    currentUserDetails.value.countryCodeID,
  );
};
const gridHeight = computed(() => {
  return `height: ${props.gridHeight};`;
});

const disableButtons = computed(() => selectedRecord.value === undefined);

const menuItems = computed(() => {
  const menu: ContextMenuItem[] = [
    { key: "new", text: "New", icon: "fas fa-plus" },
    {
      key: "edit",
      text: "Edit",
      icon: "fas fa-pencil",
      disabled: disableButtons.value,
    },
    {
      key: "delete",
      text: "Delete",
      icon: "fas fa-trash",
      disabled: disableButtons.value,
    },
  ];
  return menu;
});

const onMenuItemClicked = async (key: string) => {
  switch (key) {
    case "new":
      await handleAddClick();
      break;
    case "edit":
      await handleEditClick();
      break;
    case "delete":
      await handleDeleteClick();
      break;
    default:
      break;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(DxButton), {
          icon: "fas fa-plus",
          onClick: handleAddClick
        }),
        _createVNode(GridMenu, {
          "menu-items": menuItems.value,
          onItemClicked: onMenuItemClicked,
          "button-icon": "fas fa-bars",
          id: contextMenuId.value
        }, null, 8, ["menu-items", "id"])
      ])
    ]),
    _createElementVNode("div", {
      style: _normalizeStyle(gridHeight.value)
    }, [
      _createVNode(GridBase, {
        "data-source": setupFeeGridItems.value,
        "id-column-name": "productId",
        "column-auto-width": true,
        loading: loading.value,
        onRowClick: onRowClick,
        onAdaptiveRowSelection: onRowSelected,
        onRowSelected: onRowSelected,
        wordWrapEnabled: true,
        showSearchPanel: false,
        "custom-column-slots": ['rateCell', 'netCell', 'vatCell', 'amountCell']
      }, {
        columns: _withCtx(() => [
          _createVNode(_unref(DxColumn), {
            "data-field": "productId",
            caption: "ID",
            "hiding-priority": 1,
            visible: false
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "productDescription",
            caption: "Product",
            width: 280,
            "hiding-priority": 10
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "taxRate",
            caption: "Tax Rate",
            width: 150,
            "hiding-priority": 9,
            "cell-template": "rateCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "net",
            caption: "Net",
            "hiding-priority": 8,
            width: 100,
            "cell-template": "netCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "vat",
            caption: "VAT",
            "hiding-priority": 7,
            width: 100,
            "cell-template": "vatCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "gross",
            caption: "Amount",
            "hiding-priority": 5,
            "cell-template": "amountCell"
          })
        ]),
        rateCellData: _withCtx(({ data }) => [
          _createElementVNode("div", null, _toDisplayString(data.taxRate.toFixed(2)), 1)
        ]),
        netCellData: _withCtx(({ data }) => [
          _createElementVNode("div", null, _toDisplayString(formatNetValue(data)), 1)
        ]),
        vatCellData: _withCtx(({ data }) => [
          _createElementVNode("div", null, _toDisplayString(formatVATValue(data)), 1)
        ]),
        amountCellData: _withCtx(({ data }) => [
          _createElementVNode("div", null, _toDisplayString(formatGrossValue(data)), 1)
        ]),
        _: 1
      }, 8, ["data-source", "loading"])
    ], 4),
    (_openBlock(), _createBlock(SetupFeeModal, {
      modelValue: displaySetupFeeModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displaySetupFeeModal).value = $event)),
      passedSetupFeeGridItem: selectedRecord.value,
      onUpdateChange: handleFeeModalChange,
      key: keySetupFeeModal.value
    }, null, 8, ["modelValue", "passedSetupFeeGridItem"]))
  ], 64))
}
}

})